import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiRecordDatasource as OuiDatasource,
  OuiRowComponent,
  OuiStackComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import { Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { DownloadButtonComponent } from '../../components/download-button/download-button.component';
import { LogTableComponent } from '../../components/log-table/log-table.component';
import { PageComponent } from '../../components/page/page';
import { SelectComponent } from '../../components/select/select.component';
import { Option } from '../../models/common.models';
import { WorkflowLogEntry } from '../../models/workflow-history.model';
import { InventoryService } from '../../services/inventory/inventory.service';
import { SalesChannelService } from '../../services/sales-channel/sales-channel.service';
import { WarehouseManagementSystemService } from '../../services/warehouse-management-system/warehouse-management-system.service';
import { WarehouseService } from '../../services/warehouse/warehouse.service';
import { BasePageComponent } from '../base/base.component';

@Component({
  selector: 'app-inventory',
  standalone: true,
  imports: [
    OuiBoxComponent,
    OuiStackComponent,
    OuiCenterComponent,
    OuiTextComponent,
    OuiButtonComponent,
    SelectComponent,
    DownloadButtonComponent,
    LogTableComponent,
    PageComponent,
    OuiRowComponent,
  ],
  templateUrl: './inventory.component.html',
})
export class InventoryPage extends BasePageComponent {
  public wms = inject(WarehouseManagementSystemService);
  public warehouse = inject(WarehouseService);
  public inventoryService = inject(InventoryService);
  public salesChannel = inject(SalesChannelService);

  private readonly notification = inject(NotificationService);
  permissionToSync = this.permissionTo('inventory:manage');

  dataSyncHistoryDatasource!: OuiDatasource<WorkflowLogEntry>;
  salesChannelSyncHistoryDatasource!: OuiDatasource<WorkflowLogEntry>;

  downloadForm = new FormGroup({
    warehouseManagementSystem: new FormControl(''),
  });

  selectedWms = toSignal(
    this.downloadForm.controls.warehouseManagementSystem.valueChanges,
  );

  syncForm = new FormGroup({
    warehouseManagementSystem: new FormControl(''),
  });

  selectedSyncWms = toSignal(
    this.syncForm.controls.warehouseManagementSystem.valueChanges,
  );

  syncSalesChannelForm = new FormGroup({
    salesChannelCode: new FormControl(''),
  });

  selectedSyncSalesChannel = toSignal(
    this.syncSalesChannelForm.controls.salesChannelCode.valueChanges,
  );

  salesChannelOptions = computed(() =>
    this.salesChannel
      .salesChannelOptions()
      .filter((option) => option.value.toUpperCase() !== 'LINC'),
  );

  salesChannelPlaceholder = computed(() => {
    if (!this.regions.selectedRegionId()) {
      return 'select a region first';
    }

    if (!this.salesChannelOptions().length) {
      return 'no sales channels available';
    }

    return 'select a Sales Channel';
  });

  syncOptions = computed(() => {
    const options: Option[] = this.wms.warehouseManagementSystemOptions();

    return [{ label: 'All Systems', value: '' }, ...options];
  });

  constructor() {
    super();

    this.inventoryService.dataSyncHistory.reload();
    this.inventoryService.salesChannelSyncHistory.reload();

    effect(() => {
      const data = this.inventoryService.dataSyncHistory.value();
      if (!data) return;
      this.dataSyncHistoryDatasource = new OuiDatasource(data);
    });

    effect(() => {
      const data = this.inventoryService.salesChannelSyncHistory.value();
      if (!data) return;
      this.salesChannelSyncHistoryDatasource = new OuiDatasource(data);
    });
  }

  syncData() {
    this.syncInventory(this.selectedSyncWms());
  }

  syncSalesChannelData() {
    const channelCode = this.selectedSyncSalesChannel();
    if (!channelCode) return;

    this.salesChannel.syncSalesChannel(channelCode).subscribe({
      next: () => {
        this.notification.showSnackbar({
          message: 'Sales Channel sync completed succesfully',
          variant: 'success',
          symbolName: 'check_circle',
        });
      },
      error: (err) => {
        this.notification.showSnackbar({
          message:
            err.error || 'Failed to sync inventory data. Please try again.',
          variant: 'warn',
          symbolName: 'error',
        });
      },
    });
  }

  private syncInventory(
    wmsCode: string | null | undefined,
    salesChannelCode?: string | null,
  ) {
    this.inventoryService
      .syncInventoryForUser(wmsCode, salesChannelCode)
      .subscribe({
        next: (response) => {
          if (response.results?.length > 0) {
            if (response.results.every((result) => result.success)) {
              const successMessage = salesChannelCode
                ? 'Sales Channel sync completed succesfully'
                : `${wmsCode || 'All Systems'} sync completed successfully.`;

              this.notification.showSnackbar({
                message: successMessage,
                variant: 'success',
                symbolName: 'check_circle',
              });
              return;
            }
            if (response.results.some((result) => !result.success)) {
              const errorMessages = response.results
                .map(
                  (result) =>
                    result.error ||
                    'Failed to sync inventory. Please try again later.',
                )
                .join(', ');
              this.notification.showSnackbar({
                message: errorMessages,
                variant: 'warn',
                symbolName: 'error',
              });
              return;
            }
          } else {
            this.notification.showSnackbar({
              message: Array.isArray(response.errors)
                ? response.errors.join(', ')
                : response.errors ||
                  'Failed to sync inventory. Please try again later.',
              variant: 'warn',
              symbolName: 'warning',
            });
          }
        },
        error: (err) => {
          this.notification.showSnackbar({
            message:
              err.error || 'Failed to sync inventory data. Please try again.',
            variant: 'warn',
            symbolName: 'error',
          });
        },
      });
  }

  getInventoryDownloadFilename() {
    return `Inventory_for_${this.selectedWms() || 'All_WMS'}_${new Date().toISOString().split('T')[0]}`;
  }
}
